import { createContext, useContext, useMemo } from 'react'

import { SimulacaoContentSkeleton } from '@/features/simulador/brasil/components/simulacao-content/simulacao-content.skeleton'
import { useFluxoPagamento, useSimulacaoPorId, mapperSimulacaoResponse } from '@/features/simulador/brasil/hooks'
import { ApiErrorDisplay } from '@/shared/api-error-display'
import type { TGetSimulacaoResponse } from '@/types/simulacao'

type TIniciarAtendimentoFn = (clienteId: string) => Promise<void>
type TGetSimulacaoSuccess = NonNullable<ReturnType<typeof useSimulacaoPorId>['data']>
interface SimulacaoContentContextValue {
  simulacao: TGetSimulacaoSuccess
  fluxoPagamento: ReturnType<typeof useFluxoPagamento>['data']
  handleIniciarAtendimento?: TIniciarAtendimentoFn
}

interface ISimulacaoContentProvider {
  children: React.ReactNode
  value:
    | { tipo: 'Cache'; simulacao: TGetSimulacaoResponse; handleIniciarAtendimento?: TIniciarAtendimentoFn }
    | { tipo: 'Fetch'; simulacaoId: string; handleIniciarAtendimento?: TIniciarAtendimentoFn }
}

const SimulacaoContentContext = createContext<SimulacaoContentContextValue | null>(null)

export const useSimulacaoContentContext = () => {
  const context = useContext(SimulacaoContentContext)
  if (!context) {
    throw new Error('useSimulacaoContentContext must be used within SimulacaoContentProvider')
  }
  return context
}

export const SimulacaoContentProvider = ({ children, value }: ISimulacaoContentProvider) => {
  const simulacaoId = value.tipo === 'Fetch' ? value.simulacaoId : value.simulacao.id
  const deveRequisitar = value.tipo === 'Fetch'

  const { data: fluxoPagamento } = useFluxoPagamento([simulacaoId])

  const { data: _simulacao, error, refetch, isLoading, isPending } = useSimulacaoPorId(simulacaoId, deveRequisitar)
  const isCarregando = value.tipo === 'Fetch' && (isLoading || isPending)

  const simulacao =
    value.tipo === 'Cache' ? mapperSimulacaoResponse(value.simulacao) : (_simulacao as TGetSimulacaoSuccess)

  const contextValue = useMemo(
    () => ({ simulacao, fluxoPagamento, handleIniciarAtendimento: value.handleIniciarAtendimento }),
    [simulacao, fluxoPagamento, value.handleIniciarAtendimento],
  )

  if (isCarregando) return <SimulacaoContentSkeleton />
  if (error) return <ApiErrorDisplay error={error} resetErrorBoundary={refetch} />

  return (
    <SimulacaoContentContext.Provider value={contextValue}>
      <div className="bg-foreground border-muted border p-6">{children}</div>
    </SimulacaoContentContext.Provider>
  )
}
